import {
    FindItemJsonRequest,
    Item,
    FindItemJsonResponse,
    FindItemResponseMessage,
    getServiceAsync,
    TFactories,
} from '../../../service';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import {
    runOwsRequestWithMonitor,
    Feature,
    PersonType,
    DeletedPersona,
    DeletedPersonType,
    uniq,
} from '../../../shared';

export type DeletedContactsData = {
    deletedItems: DeletedPersona[];
    recoverableItems: DeletedPersona[];
    folderIds: string[];
};

const MAX_ENTRIES_RETURNED = 200;

function createfindItemsJsonRequest(
    offset: number,
    serviceFactory: TFactories
): FindItemJsonRequest {
    return {
        Header: getJsonRequestHeader(),
        Body: serviceFactory.findItemRequest({
            ItemShape: serviceFactory.itemResponseShape({
                BaseShape: 'IdOnly',
            }),
            Traversal: 'Shallow',
            ParentFolderIds: [
                serviceFactory.distinguishedFolderId({ Id: 'recoverableitemsdeletions' }),
                serviceFactory.distinguishedFolderId({ Id: 'deleteditems' }),
            ],
            Paging: serviceFactory.indexedPageView({
                BasePoint: 'Beginning',
                Offset: offset,
                MaxEntriesReturned: MAX_ENTRIES_RETURNED,
            }),
            ViewFilter: 'All',
            Grouping: null,
            IsWarmUpSearch: false,
            ShapeName: 'MailListItem',
            SortOrder: [
                serviceFactory.sortResults({
                    Order: 'Descending',
                    Path: serviceFactory.propertyUri({ FieldURI: 'DateTimeReceived' }),
                }),
            ],
            Restriction: {
                Item: serviceFactory.orType({
                    Items: [
                        serviceFactory.isEqualType({
                            Item: serviceFactory.propertyUri({
                                FieldURI: 'ItemClass',
                            }),
                            FieldURIOrConstant: serviceFactory.fieldURIOrConstantType({
                                Item: serviceFactory.constantType({
                                    Value: 'IPM.Contact',
                                }),
                            }),
                        }),
                        serviceFactory.isEqualType({
                            Item: serviceFactory.propertyUri({
                                FieldURI: 'ItemClass',
                            }),
                            FieldURIOrConstant: serviceFactory.fieldURIOrConstantType({
                                Item: serviceFactory.constantType({
                                    Value: 'IPM.DistList',
                                }),
                            }),
                        }),
                    ],
                }),
            },
        }),
    };
}

export const deletedItemToPersonOrPDL = (
    item: Item,
    deletedType: DeletedPersonType
): DeletedPersona => {
    const deletedDate = item.LastModifiedTime;
    const personaId = item.ItemId.Id;
    const hubPersonaId = personaId;
    const itemId = personaId;
    if (item.ItemClass === 'IPM.Contact') {
        return {
            type: PersonType.Person,
            personaId,
            hubPersonaId,
            info: {
                displayNameFirstLast: item.Subject,
                displayNameLastFirst: item.Subject,
                allEmailAddresses: [item.Subject],
                originalDisplayName: item.Subject,
            },
            deletedDate,
            deletedType,
        };
    } else {
        return {
            type: PersonType.PrivateDistributionList,
            personaId,
            hubPersonaId,
            itemId,
            info: {
                displayNameFirstLast: item.Subject,
                displayNameLastFirst: item.Subject,
                listOriginalDisplayName: item.Subject,
            },
            deletedDate,
            deletedType,
        };
    }
};

export function getDeletedContactsData(): Promise<DeletedContactsData> {
    const deletedContactsChain = (offset: number = 0, prevResults?: DeletedContactsData) => {
        return getServiceAsync().then(service =>
            runOwsRequestWithMonitor({
                scenarioName: 'FindRecoverableItems',
                feature: Feature.RestoreDeletedContacts,
                operation: () =>
                    service.Operation.findItemOperation(
                        service.Factory.findItemJsonRequest(
                            createfindItemsJsonRequest(offset, service.Factory)
                        )
                    ),
                transformResponse: (owsResponse: FindItemJsonResponse) => {
                    const folderIds: string[] = [];
                    const responses = owsResponse.Body.ResponseMessages
                        .Items as FindItemResponseMessage[];
                    const recoverableItems = [];
                    const deletedItems = [];
                    const [recoverableItemsResponse, deletedItemsResponse] = responses;
                    if (recoverableItemsResponse.RootFolder?.Items) {
                        recoverableItems.push(
                            ...recoverableItemsResponse.RootFolder.Items.map(item =>
                                deletedItemToPersonOrPDL(item, DeletedPersonType.RecoverableItem)
                            )
                        );
                        if (recoverableItemsResponse.RootFolder.Items.length > 0) {
                            folderIds.push(
                                recoverableItemsResponse.RootFolder.Items[0].ParentFolderId.Id
                            );
                        }
                    }
                    if (deletedItemsResponse.RootFolder?.Items) {
                        deletedItems.push(
                            ...deletedItemsResponse.RootFolder.Items.map(item =>
                                deletedItemToPersonOrPDL(item, DeletedPersonType.DeletedItem)
                            )
                        );
                        if (deletedItemsResponse.RootFolder.Items.length > 0) {
                            folderIds.push(
                                deletedItemsResponse.RootFolder.Items[0].ParentFolderId.Id
                            );
                        }
                    }
                    return {
                        deletedItems,
                        recoverableItems,
                        folderIds,
                    };
                },
            }).then(result => {
                const newResult: DeletedContactsData = prevResults
                    ? {
                          deletedItems: [...prevResults.deletedItems, ...result.deletedItems],
                          recoverableItems: [
                              ...prevResults.recoverableItems,
                              ...result.recoverableItems,
                          ],
                          folderIds: uniq([...prevResults.folderIds, ...result.folderIds]),
                      }
                    : result;
                if (result.deletedItems.length === 0 && result.recoverableItems.length === 0) {
                    return newResult;
                }
                return deletedContactsChain(offset + MAX_ENTRIES_RETURNED, newResult);
            })
        );
    };
    return deletedContactsChain();
}
